/**
 * @fileoverview Model coordinates -> CSS pixels through PyMOL's 18-float view, in JavaScript.
 *
 * The same mapping as pymol-web `js/projection.js` (which is not importable here: the wrapper is
 * loaded from the payload at run time, never bundled). Used for what PyMOL does not do for us:
 * placing the label overlay, and hover / click hit-tests on shapes and temporary molecules.
 *
 * PyMOL's view (cmd.get_view):
 *   v[0..8]   3x3 rotation, column-major, model -> camera
 *   v[9..11]  camera position (z negative = distance), applied after the rotation
 *   v[12..14] origin of rotation (model space)
 *   v[15]     front clipping plane, v[16] back clipping plane (distances from the camera)
 *   v[17]     +-field of view in degrees: NEGATIVE = perspective, POSITIVE = orthoscopic
 * Screen: vertical field of view |v[17]| (20 when the view carries none), y from the TOP.
 */

const DefaultFov = 20;

/** @param {ArrayLike<number>} view @returns {number} vertical field of view in degrees */
export function viewFov(view) {
    return Math.abs(view[17]) > 1 ? Math.abs(view[17]) : DefaultFov;
}

/**
 * A projector for one view and one viewport size.
 * @param {ArrayLike<number>} view 18 floats
 * @param {number} width CSS pixels
 * @param {number} height CSS pixels
 * @returns {(x: number, y: number, z: number) =>
 *   {x: number, y: number, depth: number, scale: number}}
 *   `depth` = distance in front of the camera (Angstrom, <= 0 = behind it), `scale` = CSS pixels
 *   per Angstrom at that depth
 */
export function makeProjector(view, width, height) {
    const t = Math.tan((viewFov(view) * Math.PI) / 360);
    const aspect = width / height;
    const ortho = view[17] > 0;
    const [r0, r1, r2, r3, r4, r5, r6, r7, r8, tx, ty, tz, ox, oy, oz] = view;
    return (x, y, z) => {
        const dx = x - ox;
        const dy = y - oy;
        const dz = z - oz;
        const cx = r0 * dx + r3 * dy + r6 * dz + tx;
        const cy = r1 * dx + r4 * dy + r7 * dz + ty;
        const cz = r2 * dx + r5 * dy + r8 * dz + tz;
        const d = ortho ? -tz : -cz;
        return {
            x: ((cx / (d * t * aspect) + 1) / 2) * width,
            y: (1 - (cy / (d * t) + 1) / 2) * height,
            depth: -cz,
            scale: d > 0 ? height / (2 * d * t) : 0,
        };
    };
}

/** Two views (or sizes) equal? Cheap key comparison for projection caches. */
export function sameView(a, b) {
    if (!a || !b || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
    }
    return true;
}

/**
 * The current projection of a PyMolViewer (or a test double with getView(), canvas and dpr).
 * @returns {{view: number[], width: number, height: number, project: function}|null}
 */
export function viewerProjection(viewer) {
    if (!viewer || typeof viewer.getView !== 'function') return null;
    const view = Array.from(viewer.getView());
    const dpr = viewer.dpr || 1;
    const canvas = viewer.canvas || {};
    const width = (canvas.width || 0) / dpr;
    const height = (canvas.height || 0) / dpr;
    if (view.length !== 18 || !(width > 0) || !(height > 0)) return null;
    return {
        view, width, height, project: makeProjector(view, width, height),
    };
}

/**
 * Distance from (px, py) to the 2D segment a-b, and where along it (t in [0, 1]).
 * @returns {{dist: number, t: number}}
 */
export function pointSegment2D(px, py, ax, ay, bx, by) {
    const vx = bx - ax;
    const vy = by - ay;
    const len2 = vx * vx + vy * vy;
    let t = len2 > 0 ? ((px - ax) * vx + (py - ay) * vy) / len2 : 0;
    t = Math.max(0, Math.min(1, t));
    const qx = ax + t * vx - px;
    const qy = ay + t * vy - py;
    return { dist: Math.sqrt(qx * qx + qy * qy), t };
}
